import React, { useState } from 'react';
import { ManualGameDeposit, ManualGameDepositFilters } from '../../../types';
import { useManualGameDeposits } from '../../../hooks/useApi';
import ManualGameDepositList from './ManualGameDepositList';
import ManualGameDepositDetail from './ManualGameDepositDetail';
import ConfirmDepositModal from './ConfirmDepositModal';
import RejectDepositModal from './RejectDepositModal';
import { PageHeader } from '@kioskgaming/ui';

const ManualGameDepositsManagement: React.FC = () => {
  const [filters, setFilters] = useState<ManualGameDepositFilters>({
    page: 1,
    limit: 20,
  });
  const [selectedDeposit, setSelectedDeposit] = useState<ManualGameDeposit | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const { data: depositsData, isLoading, isFetching, error, refetch } = useManualGameDeposits(filters);

  const items = depositsData?.data?.items || [];
  const meta = depositsData?.data?.meta || {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  };

  const handleViewDeposit = (deposit: ManualGameDeposit) => {
    setSelectedDeposit(deposit);
    setShowDetail(true);
  };

  const handleCloseDetail = () => {
    setShowDetail(false);
    setSelectedDeposit(null);
  };

  const handleConfirmDeposit = (deposit: ManualGameDeposit) => {
    setSelectedDeposit(deposit);
    setShowConfirmModal(true);
  };

  const handleRejectDeposit = (deposit: ManualGameDeposit) => {
    setSelectedDeposit(deposit);
    setShowRejectModal(true);
  };

  const handleSuccess = () => {
    refetch();
  };

  const handleCloseModals = () => {
    setShowConfirmModal(false);
    setShowRejectModal(false);
    setSelectedDeposit(null);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive">Unable to load manual game deposits</p>
          <button
            onClick={() => refetch()}
            className="mt-2 text-sm text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Manual Game Deposits"
        description="Review and process manual deposit requests for game wallets (Golden Dragon, Juwa, etc.)"
      />

      {/* Deposit List */}
      <ManualGameDepositList
        items={items}
        meta={meta}
        isLoading={isLoading}
        isFetching={isFetching}
        appliedSort={{
          sortBy: meta.sortBy ?? null,
          sortOrder: meta.sortOrder ?? null,
        }}
        filters={filters}
        onFiltersChange={setFilters}
        onViewDeposit={handleViewDeposit}
        onConfirmDeposit={handleConfirmDeposit}
        onRejectDeposit={handleRejectDeposit}
        onRefresh={refetch}
      />

      {/* Detail Modal */}
      {showDetail && selectedDeposit && (
        <ManualGameDepositDetail
          deposit={selectedDeposit}
          onClose={handleCloseDetail}
          onConfirm={() => {
            handleCloseDetail();
            handleConfirmDeposit(selectedDeposit);
          }}
          onReject={() => {
            handleCloseDetail();
            handleRejectDeposit(selectedDeposit);
          }}
        />
      )}

      {/* Confirm Modal */}
      <ConfirmDepositModal
        deposit={selectedDeposit}
        isOpen={showConfirmModal}
        onClose={handleCloseModals}
        onSuccess={handleSuccess}
      />

      {/* Reject Modal */}
      <RejectDepositModal
        deposit={selectedDeposit}
        isOpen={showRejectModal}
        onClose={handleCloseModals}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

export default ManualGameDepositsManagement;
